import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, ChefHat, Clock, Flame,
  PackageCheck, Phone, Truck, X, Receipt, Undo2,
  Building2, Wallet, RotateCcw, MessageSquareWarning,
} from "lucide-react";
import api, { fileUrl } from "../lib/api";
import AssignDriverPanel from "../components/AssignDriverPanel";
import DriverHint from "../components/DriverHint";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";

/**
 * Phase 4 · Vendor Order Center.
 *
 * Live kanban-style board that polls every 10s. Shows strict-state-machine
 * columns (New · Accepted · Preparing · Ready · Delivery · Completed ·
 * Cancelled) + a separate "Issues" lane that surfaces any order flagged as
 * problematic regardless of its lifecycle status.
 */

const COLUMNS = [
  { key: "new", label: "New", accent: "from-pink-500 to-rose-500", icon: Flame },
  { key: "accepted", label: "Accepted", accent: "from-amber-400 to-orange-500", icon: CheckCircle2 },
  { key: "preparing", label: "Preparing", accent: "from-cyan-400 to-blue-500", icon: ChefHat },
  { key: "ready", label: "Ready", accent: "from-emerald-400 to-teal-500", icon: PackageCheck },
  { key: "out_for_delivery", label: "Delivery", accent: "from-violet-400 to-fuchsia-500", icon: Truck },
  { key: "completed", label: "Completed", accent: "from-zinc-500 to-zinc-600", icon: CheckCircle2 },
  { key: "cancelled", label: "Cancelled", accent: "from-red-500 to-rose-600", icon: X },
];

function fmtCents(c) {
  if (c == null) return "$0.00";
  return `$${(Number(c) / 100).toFixed(2)}`;
}

function fmtAge(sec) {
  if (sec == null) return "";
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

function ageBadge(sec, status) {
  // Highlight orders that have been sitting too long. New > 2m, Accepted > 10m, Preparing > 15m.
  const thresholds = { new: 120, accepted: 600, preparing: 900 };
  const limit = thresholds[status];
  if (limit && sec > limit) return "text-red-300 bg-red-500/10 border-red-500/30";
  if (limit && sec > limit * 0.6) return "text-amber-300 bg-amber-500/10 border-amber-500/30";
  return "text-zinc-400 bg-white/5 border-white/10";
}

export default function VendorOrderCenter() {
  const navigate = useNavigate();
  const [board, setBoard] = useState({ orders: [], counts: {}, issues_count: 0 });
  const [loading, setLoading] = useState(true);
  const [includeDone, setIncludeDone] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [newCount, setNewCount] = useState(0);
  const prevNewIdsRef = useRef(new Set());
  const audioRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/vendor/orders?include_completed=${includeDone}`);
      setBoard(data);
      // Detect freshly-arrived orders and play a soft alert.
      const liveNew = new Set(data.orders.filter((o) => o.status === "new").map((o) => o.order_id));
      let added = 0;
      liveNew.forEach((id) => { if (!prevNewIdsRef.current.has(id)) added += 1; });
      if (added > 0 && prevNewIdsRef.current.size > 0) {
        toast.message(`${added} new order${added > 1 ? "s" : ""} just came in`);
        try { audioRef.current?.play?.(); } catch { /* ignore audio permission */ }
      }
      prevNewIdsRef.current = liveNew;
      setNewCount(data.counts?.new || 0);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
    } finally {
      setLoading(false);
    }
  }, [includeDone, navigate]);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  const columnOrders = useMemo(() => {
    const by = {};
    COLUMNS.forEach((c) => (by[c.key] = []));
    for (const o of board.orders) {
      if (!includeDone && (o.status === "completed" || o.status === "cancelled")) continue;
      if (by[o.status]) by[o.status].push(o);
    }
    return by;
  }, [board.orders, includeDone]);

  const issuesOrders = useMemo(() => board.orders.filter((o) => o.has_issue), [board.orders]);

  return (
    <div className="min-h-screen bg-black text-white">
      <audio ref={audioRef} preload="auto" src="data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=" />

      {/* Header */}
      <div className="sticky top-0 z-30 backdrop-blur bg-black/80 border-b border-white/10 px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate("/sell")}
          className="w-10 h-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10"
          data-testid="voc-back-btn"
        >
          <Building2 className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-black tracking-tight">Order Center</h1>
          <p className="text-xs text-zinc-500">
            {loading ? "Loading…" : `${board.orders.length} orders · ${board.issues_count} issues · auto-refresh 10s`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {newCount > 0 && (
            <span className="px-3 py-1.5 rounded-full bg-pink-500/20 border border-pink-500/40 text-pink-200 text-xs font-bold flex items-center gap-1.5" data-testid="voc-new-count">
              <Flame className="w-3 h-3" /> {newCount} new
            </span>
          )}
          <button
            onClick={() => setIncludeDone((v) => !v)}
            className={`px-3 py-1.5 rounded-full text-xs border ${includeDone ? "bg-white/10 border-white/20 text-white" : "bg-white/5 border-white/10 text-zinc-400"}`}
            data-testid="voc-toggle-done"
          >
            {includeDone ? "Hiding archive" : "Show archive"}
          </button>
          <button
            onClick={load}
            className="w-10 h-10 grid place-items-center rounded-full bg-white/5 hover:bg-white/10"
            data-testid="voc-refresh"
            title="Refresh"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <DriverHint show={!loading && board.orders.length === 0} />

      {/* Issues strip */}
      {issuesOrders.length > 0 && (
        <div className="px-6 pt-4" data-testid="voc-issues-strip">
          <div className="rounded-2xl bg-red-500/10 border border-red-500/40 p-4">
            <div className="flex items-center gap-2 text-red-200 mb-2">
              <AlertTriangle className="w-4 h-4" />
              <h2 className="text-sm font-bold uppercase tracking-wider">Issues ({issuesOrders.length})</h2>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {issuesOrders.map((o) => (
                <button
                  key={o.order_id}
                  onClick={() => setSelectedOrderId(o.order_id)}
                  className="shrink-0 px-3 py-2 rounded-xl bg-red-500/20 border border-red-500/30 text-left"
                  data-testid={`voc-issue-${o.order_id}`}
                >
                  <div className="text-xs text-red-200">#{o.short_code} · {o.buyer_name}</div>
                  <div className="text-[11px] text-red-100/80 truncate max-w-[220px]">{o.notes || o.status}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Columns */}
      <div className="px-6 py-6 overflow-x-auto">
        <div className="flex gap-4 min-w-max pb-6">
          {COLUMNS.map((col) => {
            const items = columnOrders[col.key] || [];
            const Icon = col.icon;
            return (
              <div
                key={col.key}
                className="w-80 shrink-0 flex flex-col gap-3"
                data-testid={`voc-col-${col.key}`}
              >
                <div className={`rounded-xl border border-white/10 bg-zinc-900 px-4 py-3`}>
                  <div className="flex items-center gap-2">
                    <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${col.accent} grid place-items-center`}>
                      <Icon className="w-3.5 h-3.5 text-white" />
                    </div>
                    <h3 className="font-bold">{col.label}</h3>
                    <span className="ml-auto text-xs text-zinc-400">{items.length}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-3">
                  {items.length === 0 ? (
                    <p className="text-center text-xs text-zinc-600 py-6">—</p>
                  ) : (
                    items.map((o) => (
                      <OrderCard key={o.order_id} order={o} onOpen={() => setSelectedOrderId(o.order_id)} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <OrderDetailSheet
        orderId={selectedOrderId}
        onClose={() => setSelectedOrderId(null)}
        onChanged={load}
      />
    </div>
  );
}

function OrderCard({ order, onOpen }) {
  return (
    <button
      onClick={onOpen}
      className="text-left bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 rounded-2xl p-4 transition-colors"
      data-testid={`voc-card-${order.order_id}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-xs font-bold text-zinc-200">#{order.short_code}</span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] border ${ageBadge(order.age_seconds, order.status)} flex items-center gap-1`}>
          <Clock className="w-3 h-3" /> {fmtAge(order.age_seconds)}
        </span>
      </div>
      <div className="flex items-center gap-2 text-xs text-zinc-400 mb-2">
        <span className="truncate flex-1">{order.buyer_name}</span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${order.fulfillment_type === "delivery" ? "bg-violet-500/15 text-violet-200 border border-violet-500/30" : order.fulfillment_type === "dine_in" ? "bg-sky-500/15 text-sky-200 border border-sky-500/30" : "bg-emerald-500/15 text-emerald-200 border border-emerald-500/30"}`}>
          {order.fulfillment_type}
        </span>
      </div>
      <div className="space-y-1 mt-2">
        {order.items.slice(0, 3).map((it) => (
          <div key={it.line_id || it.product_id} className="text-sm">
            <div className="flex items-center gap-2">
              <span className="text-pink-300 font-bold w-5 shrink-0">×{it.quantity}</span>
              <span className="truncate">{it.name}</span>
            </div>
            {(it.customization_summary?.variation || it.customization_summary?.modifiers?.length || it.customization_summary?.ingredients?.length) && (
              <div className="pl-7 text-[11px] text-zinc-500 truncate">
                {[
                  it.customization_summary?.variation,
                  ...(it.customization_summary?.modifiers || []),
                  ...(it.customization_summary?.ingredients || []),
                ].filter(Boolean).join(" · ")}
              </div>
            )}
            {it.customization_summary?.special_instructions && (
              <div className="pl-7 text-[11px] text-pink-300 italic truncate">"{it.customization_summary.special_instructions}"</div>
            )}
          </div>
        ))}
        {order.items.length > 3 && (
          <div className="text-[11px] text-zinc-500 pl-7">+ {order.items.length - 3} more</div>
        )}
      </div>
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-white/5">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">
          {order.payment_status === "paid" ? "Paid" :
           order.payment_status === "on_arrival" ? "Pay on arrival" :
           order.payment_status === "pending_payment" ? "Awaiting pay" :
           order.payment_status === "refunded" ? "Refunded" :
           order.payment_status === "partially_refunded" ? "Partial refund" :
           order.payment_method?.toUpperCase() || ""}
        </span>
        <span className="font-black text-sm">{fmtCents(order.total_cents)}</span>
      </div>
      {order.has_issue && (
        <div className="mt-2 flex items-center gap-1 text-[10px] text-red-300 font-semibold">
          <AlertTriangle className="w-3 h-3" /> Flagged issue
        </div>
      )}
    </button>
  );
}

function OrderDetailSheet({ orderId, onClose, onChanged }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);

  const load = useCallback(async () => {
    if (!orderId) { setOrder(null); return; }
    try {
      const { data } = await api.get(`/vendor/orders/${orderId}`);
      setOrder(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load order");
      onClose();
    }
  }, [orderId, onClose]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vendor/orders/${orderId}${path}`, body);
      setOrder({ ...data, role: order?.role, actions: order?.actions });
      await load();
      onChanged?.();
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const open = Boolean(orderId);
  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <aside
        className="fixed right-0 top-0 bottom-0 z-50 w-full sm:max-w-xl bg-zinc-950 border-l border-white/10 overflow-y-auto"
        data-testid="voc-detail-sheet"
      >
        <div className="sticky top-0 bg-zinc-950/95 backdrop-blur px-6 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-black">#{order?.order_id?.slice(-6).toUpperCase() || "…"}</h2>
            <p className="text-xs text-zinc-500">{order?.buyer_name}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/10" data-testid="voc-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {!order ? (
          <div className="p-6 text-sm text-zinc-500">Loading…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Status banner */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Status</div>
                <div className="text-xl font-black capitalize">{order.status.replace("_", " ")}</div>
                {order.has_issue && (
                  <div className="mt-1 text-xs text-red-300 font-semibold flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> {order.issue_reason}
                  </div>
                )}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Total</div>
                <div className="text-2xl font-black">{fmtCents(order.total_cents)}</div>
                {order.refunded_cents > 0 && (
                  <div className="text-xs text-amber-300">Refunded {fmtCents(order.refunded_cents)}</div>
                )}
              </div>
            </div>

            {/* Fulfillment */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoTile label="Fulfillment" value={order.fulfillment_type?.toUpperCase()} />
              <InfoTile label="Payment" value={paymentLabel(order)} />
              {order.scheduled_for && <InfoTile label="Scheduled" value={new Date(order.scheduled_for).toLocaleString()} />}
              {order.delivery_address && <InfoTile label="Address" value={order.delivery_address} />}
              {order.contact_phone && (
                <a href={`tel:${order.contact_phone}`} className="col-span-2 flex items-center gap-2 bg-white/5 hover:bg-white/10 px-4 py-3 rounded-xl text-sm">
                  <Phone className="w-4 h-4 text-pink-300" />
                  <span className="font-semibold">{order.contact_phone}</span>
                </a>
              )}
            </div>

            {order.location_label && <InfoTile label="Location" value={order.location_label} />}
            <AssignDriverPanel order={order} onChanged={() => { load(); onChanged?.(); }} />

            {/* Items */}
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Order items</h3>
              <div className="space-y-3">
                {order.items.map((it) => {
                  const cs = it.customization_summary || {};
                  return (
                    <div key={it.line_id || it.product_id} className="flex gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5" data-testid={`voc-line-${it.line_id || it.product_id}`}>
                      <div className="w-14 h-14 rounded-lg bg-zinc-900 overflow-hidden shrink-0">
                        {it.image_paths?.[0] && <img src={fileUrl(it.image_paths[0])} alt={it.name} className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-pink-300 font-bold shrink-0">×{it.quantity}</span>
                            <span className="font-semibold truncate">{it.name}</span>
                          </div>
                          <span className="text-sm font-bold whitespace-nowrap">{fmtCents(it.line_total_cents)}</span>
                        </div>
                        {cs.variation && <div className="text-xs text-zinc-400 mt-1">Size: {cs.variation}</div>}
                        {(cs.modifiers || []).length > 0 && (
                          <div className="text-xs text-zinc-400 mt-0.5">+ {(cs.modifiers || []).join(", ")}</div>
                        )}
                        {(cs.ingredients || []).length > 0 && (
                          <div className="text-xs text-amber-300 mt-0.5">{(cs.ingredients || []).join(" · ")}</div>
                        )}
                        {cs.special_instructions && (
                          <div className="mt-1.5 text-xs italic text-pink-300 bg-pink-500/10 border border-pink-500/30 rounded-lg px-2 py-1" data-testid={`voc-instr-${it.line_id || it.product_id}`}>
                            "{cs.special_instructions}"
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Totals */}
            <div className="rounded-xl bg-white/[0.03] border border-white/5 p-4 space-y-1 text-sm">
              <RowKV label="Subtotal" value={fmtCents(order.subtotal_cents)} />
              {order.service_fee_cents > 0 && <RowKV label="Service fee" value={fmtCents(order.service_fee_cents)} />}
              {order.tip_cents > 0 && <RowKV label="Tip" value={fmtCents(order.tip_cents)} />}
              {order.discount_cents > 0 && <RowKV label={`Promo${order.promotion_code ? ` (${order.promotion_code})` : ""}`} value={`-${fmtCents(order.discount_cents)}`} tone="text-emerald-300" />}
              {order.loyalty_discount_cents > 0 && <RowKV label={`Loyalty (${order.loyalty_points_redeemed} pts)`} value={`-${fmtCents(order.loyalty_discount_cents)}`} tone="text-emerald-300" />}
              <div className="pt-2 border-t border-white/10 flex justify-between font-black">
                <span>Total</span><span>{fmtCents(order.total_cents)}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-3">
              {order.actions?.includes("accept") && (
                <Button disabled={busy} onClick={() => doAction("/accept")} className="h-12 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold col-span-2" data-testid="voc-action-accept">
                  Accept order
                </Button>
              )}
              {order.actions?.includes("advance") && (
                <Button disabled={busy} onClick={() => doAction("/advance")} className="h-12 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold col-span-2" data-testid="voc-action-advance">
                  {advanceLabel(order.status, order.fulfillment_type)}
                </Button>
              )}
              {order.actions?.includes("cancel") && (
                <Button disabled={busy} onClick={() => setCancelOpen(true)} variant="outline" className="h-11 rounded-full border-white/10 text-white hover:bg-white/5" data-testid="voc-action-cancel">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
              )}
              {order.actions?.includes("refund") && (
                <Button disabled={busy} onClick={() => setRefundOpen(true)} variant="outline" className="h-11 rounded-full border-white/10 text-white hover:bg-white/5" data-testid="voc-action-refund">
                  <Undo2 className="w-4 h-4 mr-1" /> Refund
                </Button>
              )}
              {order.actions?.includes("mark_issue") && (
                <Button disabled={busy} onClick={() => setIssueOpen(true)} variant="outline" className="h-11 rounded-full border-amber-500/40 text-amber-200 hover:bg-amber-500/10 col-span-2" data-testid="voc-action-issue">
                  <MessageSquareWarning className="w-4 h-4 mr-1" /> Flag issue
                </Button>
              )}
              {order.actions?.includes("resolve_issue") && (
                <Button disabled={busy} onClick={() => doAction("/resolve-issue")} variant="outline" className="h-11 rounded-full border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10 col-span-2" data-testid="voc-action-resolve">
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve issue
                </Button>
              )}
            </div>

            {/* Audit log */}
            {(order.events || []).length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Activity</h3>
                <div className="space-y-2">
                  {order.events.slice().reverse().map((ev, idx) => (
                    <div key={idx} className="text-xs text-zinc-400 flex items-start gap-2" data-testid={`voc-event-${idx}`}>
                      <Receipt className="w-3 h-3 mt-0.5 shrink-0 text-zinc-600" />
                      <div className="flex-1 min-w-0">
                        <div><span className="text-zinc-300 font-semibold">{ev.action.replace("_", " ")}</span> · {ev.actor_name}{ev.actor_role ? ` (${ev.actor_role})` : ""}</div>
                        {ev.reason && <div className="text-zinc-500">{ev.reason}</div>}
                        <div className="text-zinc-600">{new Date(ev.ts).toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </aside>

      <ReasonDialog
        open={cancelOpen} onClose={() => setCancelOpen(false)}
        title="Cancel order"
        confirmLabel="Cancel order"
        destructive
        onConfirm={async (reason) => { await doAction("/cancel", { reason }); setCancelOpen(false); }}
      />
      <RefundDialog
        open={refundOpen} onClose={() => setRefundOpen(false)}
        totalCents={order?.total_cents || 0}
        refundedCents={order?.refunded_cents || 0}
        onConfirm={async (amount, reason) => { await doAction("/refund", { reason, amount_cents: amount }); setRefundOpen(false); }}
      />
      <ReasonDialog
        open={issueOpen} onClose={() => setIssueOpen(false)}
        title="Flag an issue"
        confirmLabel="Flag issue"
        onConfirm={async (reason) => { await doAction("/mark-issue", { reason }); setIssueOpen(false); }}
      />
    </>
  );
}

function paymentLabel(order) {
  switch (order.payment_status) {
    case "paid": return "Paid online";
    case "on_arrival": return "Pay on arrival";
    case "pending_payment": return "Awaiting pay";
    case "refunded": return "Refunded";
    case "partially_refunded": return "Partial refund";
    default: return `${order.payment_method} handle`;
  }
}

function advanceLabel(status, fulfillment) {
  switch (status) {
    case "accepted": return "Start preparing";
    case "preparing": return "Mark as ready";
    case "ready": return fulfillment === "delivery" ? "Hand to courier" : "Mark completed";
    case "out_for_delivery": return "Mark completed";
    default: return "Advance";
  }
}

function InfoTile({ label, value }) {
  return (
    <div className="rounded-xl bg-white/[0.03] border border-white/5 px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-semibold mt-0.5 break-words">{value || "—"}</div>
    </div>
  );
}

function RowKV({ label, value, tone }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-400">{label}</span>
      <span className={tone || ""}>{value}</span>
    </div>
  );
}

function ReasonDialog({ open, onClose, title, confirmLabel, onConfirm, destructive = false }) {
  const [reason, setReason] = useState("");
  useEffect(() => { if (open) setReason(""); }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-zinc-500">Tell the customer why so they know what's happening.</DialogDescription>
        </DialogHeader>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (required)"
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm resize-none"
          data-testid="voc-reason-input"
        />
        <div className="flex gap-2 justify-end mt-2">
          <Button onClick={onClose} variant="outline" className="rounded-full border-white/10 text-zinc-300">Back</Button>
          <Button
            disabled={reason.trim().length < 3}
            onClick={() => onConfirm(reason.trim())}
            className={`rounded-full ${destructive ? "bg-red-500 hover:bg-red-400" : "bg-pink-500 hover:bg-pink-400"} text-white`}
            data-testid="voc-reason-confirm"
          >
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RefundDialog({ open, onClose, totalCents, refundedCents, onConfirm }) {
  const remaining = Math.max(0, totalCents - refundedCents);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [kind, setKind] = useState("full");
  useEffect(() => { if (open) { setAmount(""); setReason(""); setKind("full"); } }, [open]);
  const amtCents = kind === "full" ? remaining : Math.round(Number(amount || 0) * 100);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-950 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle>Refund order</DialogTitle>
          <DialogDescription className="text-zinc-500">Partial refunds keep the ticket active; a full refund cancels the order.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-zinc-400">Refundable: <span className="font-bold text-white">{fmtCents(remaining)}</span></div>
          <div className="flex gap-2">
            <button
              onClick={() => setKind("full")}
              className={`flex-1 h-10 rounded-full text-sm font-semibold border ${kind === "full" ? "bg-pink-500/20 border-pink-500/60" : "bg-white/5 border-white/10"}`}
              data-testid="voc-refund-kind-full"
            >
              Full
            </button>
            <button
              onClick={() => setKind("partial")}
              className={`flex-1 h-10 rounded-full text-sm font-semibold border ${kind === "partial" ? "bg-pink-500/20 border-pink-500/60" : "bg-white/5 border-white/10"}`}
              data-testid="voc-refund-kind-partial"
            >
              Partial
            </button>
          </div>
          {kind === "partial" && (
            <input
              type="number" step="0.01" min={0.01} max={remaining / 100}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount in dollars"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm"
              data-testid="voc-refund-amount"
            />
          )}
          <textarea
            rows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (required)"
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm resize-none"
            data-testid="voc-refund-reason"
          />
          <div className="flex gap-2 justify-end">
            <Button onClick={onClose} variant="outline" className="rounded-full border-white/10 text-zinc-300">Back</Button>
            <Button
              disabled={reason.trim().length < 3 || amtCents < 1 || amtCents > remaining}
              onClick={() => onConfirm(amtCents, reason.trim())}
              className="rounded-full bg-pink-500 hover:bg-pink-400 text-white"
              data-testid="voc-refund-confirm"
            >
              <Wallet className="w-4 h-4 mr-1" /> Refund {fmtCents(amtCents)}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
